import * as z from "zod";
import { ipVersion, isNonPublicAddress } from "./network-host.js";

const dnsName = z
  .string()
  .max(253)
  .regex(
    /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/,
  );
const publicIpv4 = z
  .string()
  .refine((value) => ipVersion(value) === 4 && !isNonPublicAddress(value));

export const NodeScreenIngressConfigSchema = z
  .object({
    version: z.literal(1),
    baseDomain: dnsName,
    autoEnroll: z.boolean().default(false),
    dns: z
      .object({
        provider: z.literal("cloudflare"),
        zoneId: z.string().regex(/^[a-f0-9]{32}$/),
        zoneName: dnsName,
        apiToken: z.string().min(20).max(4096),
      })
      .strict(),
    acmeEmail: z.email().optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.baseDomain !== value.dns.zoneName &&
      value.baseDomain.endsWith(`.${value.dns.zoneName}`),
  );
export type NodeScreenIngressConfig = z.infer<typeof NodeScreenIngressConfigSchema>;

export const NodeScreenIngressStageSchema = z.enum([
  "pending",
  "dns_pending",
  "awaiting_tls",
  "verifying",
  "ready",
  "degraded",
  "disabled",
  "needs_bootstrap_repair",
  "manual_review",
  "install_result_unknown",
]);
export type NodeScreenIngressStage = z.infer<typeof NodeScreenIngressStageSchema>;

export const NodeScreenIngressErrorSchema = z.enum([
  "invalid_config",
  "unsupported_address",
  "dns_unavailable",
  "dns_conflict",
  "dns_unknown_result",
  "http_challenge_unreachable",
  "tls_unavailable",
  "ws_unavailable",
  "node_unavailable",
  "installer_incomplete",
  "certificate_failed",
  "manual_review_required",
]);
export type NodeScreenIngressError = z.infer<typeof NodeScreenIngressErrorSchema>;

const capacity = z.object({
  cpuMillis: z.number().int().positive(),
  memoryMib: z.number().int().positive(),
  storageMib: z.number().int().positive(),
});
export const NodeScreenIngressInstallReceiptSchema = z
  .object({
    version: z.literal(1),
    core: z.object({ status: z.literal("ready"), capacity }).strict(),
    ingress: z.discriminatedUnion("status", [
      z.object({ status: z.literal("prepared"), version: z.literal(1), hostname: dnsName }),
      z.object({ status: z.literal("pending"), errorCode: NodeScreenIngressErrorSchema }),
      z.object({
        status: z.literal("needs_bootstrap_repair"),
        errorCode: NodeScreenIngressErrorSchema,
      }),
    ]),
  })
  .strict();
export type NodeScreenIngressInstallReceipt = z.infer<typeof NodeScreenIngressInstallReceiptSchema>;

export const NodeScreenCertificateIssueSchema = z
  .object({
    version: z.literal(1),
    attemptId: z.string().uuid(),
    hostname: dnsName,
    generation: z.number().int().positive(),
    challengeDigest: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict();
export type NodeScreenCertificateIssue = z.infer<typeof NodeScreenCertificateIssueSchema>;
export const NodeScreenCertificatePermitSchema = z
  .object({ version: z.literal(1), attemptId: z.string().uuid(), expiresAt: z.number().int() })
  .strict();

export function screenIngressHostname(nodeId: string, baseDomain: string) {
  if (!/^[a-f0-9-]{36}$/.test(nodeId)) throw new Error("Invalid node identity for ingress");
  return dnsName.parse(`${nodeId}.${baseDomain}`);
}
export function screenIngressPublicIpv4(value: string) {
  return publicIpv4.parse(value);
}
